import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { getServices } from '../../lib/api'
import { GlowCard } from '../ui/spotlight-card'
import SectionTitle from '../ui/SectionTitle'
import ScrollReveal from '../ui/ScrollReveal'
import LoadingSpinner from '../ui/LoadingSpinner'
import GridGlowBackground from '../ui/grid-glow-background'
import type { Service } from '../../types'

const glowColorMap: Record<string, 'blue' | 'purple' | 'green' | 'red' | 'orange'> = {
  'web-design': 'blue',
  development: 'purple',
  ecommerce: 'orange',
  seo: 'green',
  maintenance: 'blue',
  branding: 'purple',
}

export default function ServicesGrid() {
  const [data, setData] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getServices().then((res) => {
      setData(res)
      setLoading(false)
    })
  }, [])

  if (loading) return <LoadingSpinner text="Loading services..." />

  return (
    <GridGlowBackground
      backgroundColor="#030712"
      gridColor="rgba(255,255,255,0.02)"
      gridSize={60}
      glowColors={['#00d4ff', '#7c3aed', '#00d4ff']}
      glowCount={6}
      className="py-24"
    >
      <section className="relative">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          label="What We Do"
          title="Full-Service Digital Agency"
          subtitle="From strategy to launch, we provide end-to-end digital solutions that drive real business results."
        />

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.map((service, i) => (
            <ScrollReveal key={service.id} delay={i * 0.08}>
              <GlowCard
                glowColor={glowColorMap[service.id] || 'blue'}
                customSize
                className="p-8 h-full backdrop-blur-[5px]"
              >
                <div
                  className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${service.gradient} p-3 mb-6 flex items-center justify-center text-2xl`}
                >
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-6">
                  {service.description}
                </p>
                <ul className="space-y-2.5">
                  {service.features.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-start gap-2.5 text-sm text-gray-400"
                    >
                      <span className="mt-0.5 w-1.5 h-1.5 rounded-full bg-electric-blue flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </GlowCard>
            </ScrollReveal>
          ))}
        </div>
      </div>
      </section>
    </GridGlowBackground>
  )
}
